/**
 * api.ts — the typed HTTP client over `business/api-types.ts`.
 *
 * Contract rules implemented here (docs/api-business.md §1):
 *  - base path `/api`, JSON in/out, credentials from the session cookie (never a token);
 *  - every mutation carries `X-Requested-With: fetch` (CSRF);
 *  - path params are `encodeURIComponent`-ed (Financy account ids may contain `=`, invoice
 *    numbers are addressed by document number);
 *  - `401 UNAUTHENTICATED` calls `onUnauthenticated()` — the app routes to /login keeping the
 *    intended route in `?next=`;
 *  - any non-2xx JSON body `{error:{code,message}}` becomes an `ApiError`, which the UI surfaces
 *    as a toast (Hebrew message from the server, or `i18n/he.ts` as a fallback).
 *
 * The generic `call()` is keyed by `ApiRouteKey`, so renaming a route in the contract fails the
 * typecheck here and in `api.mock.ts`.
 */

import type {
  Account,
  Alert,
  AlertsQuery,
  AlertsResponse,
  ApiRouteKey,
  ApiRoutes,
  CashflowResponse,
  ChangePasswordRequest,
  Connection,
  Customer,
  CustomersResponse,
  ErrorCode,
  ExpenseCategory,
  ExpenseCategorySettings,
  ExpensesQuery,
  ExpensesResponse,
  ForecastResponse,
  InvoiceDetail,
  InvoicesQuery,
  InvoicesResponse,
  LoginRequest,
  LoginResponse,
  MarkInvoicePaidRequest,
  MarkVatPeriodRequest,
  MatchInvoiceRequest,
  MatchTransactionRequest,
  MeResponse,
  OkResponse,
  OpsCallsQuery,
  OpsCallsResponse,
  OpsCustomersResponse,
  OpsSubscriptionsResponse,
  OpsSummary,
  PeriodParam,
  ReadAllAlertsRequest,
  ReadAllAlertsResponse,
  ReminderDraft,
  SettingsResponse,
  SseEvent,
  SummaryResponse,
  SuppliersResponse,
  SyncRequestResponse,
  SyncStatusResponse,
  TaxSettings,
  TestIntegrationResponse,
  TransactionDetail,
  TransactionsQuery,
  TransactionsResponse,
  UpdateAccountRequest,
  UpdateAlertRequest,
  UpdateExpenseCategoryRequest,
  UpdateExpenseRequest,
  UpdateExpenseResponse,
  UpdateSettingsRequest,
  UpdateTaxSettingsRequest,
  UpdateTransactionRequest,
  UpdateTransactionResponse,
  UsersResponse,
  VatPeriod,
  VatPeriodParam,
  VatResponse,
} from '../../api-types.ts';
import { he } from './i18n/he.ts';
import { loansFromAccounts, normalizeLoansPayload, type LoansPayload } from './loans.ts';

// ───────────────────────────── errors ─────────────────────────────

/** A failed API call. `code` is the contract's `ErrorCode`, or `NETWORK` when fetch itself failed. */
export class ApiError extends Error {
  code: ErrorCode | 'NETWORK';
  status: number;
  details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode | 'NETWORK', message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/** Hebrew message for an error, preferring the server's own text. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message || he.errors[err.code] || he.errors.INTERNAL;
  if (err instanceof Error && err.message) return err.message;
  return he.errors.INTERNAL;
}

// ───────────────────────────── route typing ─────────────────────────────

type MethodOf<K extends string> = K extends `${infer M} ${string}` ? M : never;
type PathOf<K extends string> = K extends `${string} ${infer P}` ? P : never;
type ResOf<K extends ApiRouteKey> = ApiRoutes[K]['res'];
type BodyOf<K extends ApiRouteKey> = ApiRoutes[K] extends { body?: infer B } ? B : undefined;
type QueryOf<K extends ApiRouteKey> = ApiRoutes[K] extends { query: infer Q } ? Q : undefined;

export interface CallOptions<K extends ApiRouteKey> {
  /** Values for the `:name` segments of the route path. */
  params?: Record<string, string>;
  query?: QueryOf<K>;
  body?: BodyOf<K>;
  signal?: AbortSignal;
}

function fillPath(path: string, params: Record<string, string> | undefined): string {
  return path.replace(/:([A-Za-z_]+)/g, (_m, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new ApiError('VALIDATION', `missing path param ${name}`, 0);
    return encodeURIComponent(value);
  });
}

function queryString(query: unknown): string {
  if (!query || typeof query !== 'object') return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export interface ApiOptions {
  /** Defaults to `''` (same origin). */
  baseUrl?: string;
  /** Called on `401 UNAUTHENTICATED` before the error is thrown. */
  onUnauthenticated?: () => void;
  /** Injected for tests. */
  fetchImpl?: typeof fetch;
}

// ───────────────────────────── the client surface ─────────────────────────────

/** Everything the screens may call. `api.mock.ts` implements the same interface. */
export interface Api {
  login(body: LoginRequest): Promise<LoginResponse>;
  logout(): Promise<OkResponse>;
  logoutAll(): Promise<OkResponse>;
  me(): Promise<MeResponse>;
  changePassword(body: ChangePasswordRequest): Promise<OkResponse>;

  summary(period?: PeriodParam): Promise<SummaryResponse>;
  cashflow(query?: { months?: number; period?: PeriodParam }): Promise<CashflowResponse>;
  forecast(days?: 30 | 60 | 90): Promise<ForecastResponse>;

  transactions(query: TransactionsQuery): Promise<TransactionsResponse>;
  transaction(id: string): Promise<TransactionDetail>;
  updateTransaction(id: string, body: UpdateTransactionRequest): Promise<UpdateTransactionResponse>;
  matchTransaction(id: string, body: MatchTransactionRequest): Promise<TransactionDetail>;

  invoices(query: InvoicesQuery): Promise<InvoicesResponse>;
  invoice(number: string): Promise<InvoiceDetail>;
  payInvoice(number: string, body: MarkInvoicePaidRequest): Promise<InvoiceDetail>;
  matchInvoice(number: string, body: MatchInvoiceRequest): Promise<InvoiceDetail>;
  reminderDraft(number: string): Promise<ReminderDraft>;
  recordReminder(number: string, via: 'copy' | 'mailto'): Promise<InvoiceDetail>;
  customers(): Promise<CustomersResponse>;
  customer(id: string): Promise<Customer>;

  vat(period?: VatPeriodParam): Promise<VatResponse>;
  markVatPeriod(period: string, body: MarkVatPeriodRequest): Promise<VatPeriod>;
  taxSettings(): Promise<TaxSettings>;
  updateTaxSettings(body: UpdateTaxSettingsRequest): Promise<TaxSettings>;

  expenses(query: ExpensesQuery): Promise<ExpensesResponse>;
  updateExpense(id: string, body: UpdateExpenseRequest): Promise<UpdateExpenseResponse>;
  updateExpenseCategory(key: ExpenseCategory, body: UpdateExpenseCategoryRequest): Promise<ExpenseCategorySettings>;
  suppliers(): Promise<SuppliersResponse>;

  opsSummary(): Promise<OpsSummary>;
  opsCalls(query?: OpsCallsQuery): Promise<OpsCallsResponse>;
  opsSubscriptions(): Promise<OpsSubscriptionsResponse>;
  opsCustomers(): Promise<OpsCustomersResponse>;

  accounts(): Promise<{ items: Account[] }>;
  updateAccount(id: string, body: UpdateAccountRequest): Promise<Account>;
  connections(): Promise<{ items: Connection[] }>;

  /**
   * REDESIGN §6 — the loans screen. `GET /api/loans` when the server has it; otherwise derived
   * from `GET /api/accounts`, which already carries every LOAN account with its interest, its
   * contract dates and its assignment. `asOf` decides what is open and what is still ahead, and
   * is only used by the derived path.
   */
  loans(asOf: string): Promise<LoansPayload>;

  settings(): Promise<SettingsResponse>;
  updateSettings(body: UpdateSettingsRequest): Promise<SettingsResponse>;
  testHyp(): Promise<TestIntegrationResponse>;
  testLabtop(): Promise<TestIntegrationResponse>;
  users(): Promise<UsersResponse>;

  sync(providerRefresh?: boolean): Promise<SyncRequestResponse>;
  syncStatus(): Promise<SyncStatusResponse>;

  alerts(query?: AlertsQuery): Promise<AlertsResponse>;
  updateAlert(id: string, body: UpdateAlertRequest): Promise<Alert>;
  readAllAlerts(body?: ReadAllAlertsRequest): Promise<ReadAllAlertsResponse>;

  /** SSE subscription; returns an unsubscribe function. */
  subscribe(onEvent: (event: SseEvent) => void): () => void;
  /** URL for a CSV export (a plain cookie-authenticated GET). */
  exportUrl(kind: 'transactions' | 'invoices' | 'vat', query: Record<string, string | number | undefined>): string;
  /** `true` for the mock adapter — the UI shows the demo pill and the demo chip. */
  readonly isMock: boolean;
}

// ───────────────────────────── HTTP implementation ─────────────────────────────

export function createHttpApi(options: ApiOptions = {}): Api {
  const baseUrl = options.baseUrl ?? '';
  const doFetch = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  /**
   * The transport: one place that owns the CSRF header, the cookie, the 401 hook and the
   * `{error:{code,message}}` → `ApiError` translation. `call()` is the typed door onto it; the
   * loans route (REDESIGN §6, not yet in `api-types.ts`) uses it directly and validates the body.
   */
  async function request(method: string, url: string, opts: { body?: unknown; signal?: AbortSignal } = {}): Promise<unknown> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const isMutation = method !== 'GET';
    if (isMutation) headers['X-Requested-With'] = 'fetch'; // CSRF (api-business.md §1.2)
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json; charset=utf-8';

    let response: Response;
    try {
      response = await doFetch(url, {
        method,
        headers,
        credentials: 'same-origin',
        cache: 'no-store',
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError('NETWORK', he.errors.NETWORK, 0);
    }

    if (response.status === 401) options.onUnauthenticated?.();

    const text = await response.text();
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text) as unknown;
      } catch {
        payload = null;
      }
    }

    if (!response.ok) {
      const body = payload as { error?: { code?: ErrorCode; message?: string; details?: Record<string, unknown> } } | null;
      const code = body?.error?.code ?? 'INTERNAL';
      const message = body?.error?.message || he.errors[code] || he.errors.INTERNAL;
      throw new ApiError(code, message, response.status, body?.error?.details);
    }
    return payload;
  }

  /** The typed door: an `ApiRouteKey` decides the method, the path and the response type. */
  function call<K extends ApiRouteKey>(key: K, opts: CallOptions<K> = {}): Promise<ResOf<K>> {
    const space = key.indexOf(' ');
    const method = key.slice(0, space) as MethodOf<K>;
    const path = key.slice(space + 1) as PathOf<K>;
    const url = baseUrl + fillPath(path, opts.params) + queryString(opts.query);
    return request(method, url, { body: opts.body, signal: opts.signal }) as Promise<ResOf<K>>;
  }

  return {
    login: (body) => call('POST /api/auth/login', { body }),
    logout: () => call('POST /api/auth/logout', {}),
    logoutAll: () => call('POST /api/auth/logout-all', {}),
    me: () => call('GET /api/auth/me', {}),
    changePassword: (body) => call('POST /api/auth/password', { body }),

    summary: (period) => call('GET /api/summary', { query: period ? { period } : {} }),
    cashflow: (query) => call('GET /api/cashflow', { query: query ?? {} }),
    forecast: (days) => call('GET /api/forecast', { query: days ? { days } : {} }),

    transactions: (query) => call('GET /api/transactions', { query }),
    transaction: (id) => call('GET /api/transactions/:id', { params: { id } }),
    updateTransaction: (id, body) => call('PATCH /api/transactions/:id', { params: { id }, body }),
    matchTransaction: (id, body) => call('POST /api/transactions/:id/match', { params: { id }, body }),

    invoices: (query) => call('GET /api/invoices', { query }),
    invoice: (number) => call('GET /api/invoices/:number', { params: { number } }),
    payInvoice: (number, body) => call('POST /api/invoices/:number/pay', { params: { number }, body }),
    matchInvoice: (number, body) => call('POST /api/invoices/:number/match', { params: { number }, body }),
    reminderDraft: (number) => call('POST /api/invoices/:number/reminder-draft', { params: { number } }),
    recordReminder: (number, via) => call('POST /api/invoices/:number/reminders', { params: { number }, body: { via } }),
    customers: () => call('GET /api/customers', {}),
    customer: (id) => call('GET /api/customers/:id', { params: { id } }),

    vat: (period) => call('GET /api/vat', { query: period ? { period } : {} }),
    markVatPeriod: (period, body) => call('POST /api/vat/:period/mark', { params: { period }, body }),
    taxSettings: () => call('GET /api/tax-settings', {}),
    updateTaxSettings: (body) => call('PATCH /api/tax-settings', { body }),

    expenses: (query) => call('GET /api/expenses', { query }),
    updateExpense: (id, body) => call('PATCH /api/expenses/:id', { params: { id }, body }),
    updateExpenseCategory: (key, body) => call('PATCH /api/expense-categories/:key', { params: { key }, body }),
    suppliers: () => call('GET /api/suppliers', {}),

    opsSummary: () => call('GET /api/ops/summary', {}),
    opsCalls: (query) => call('GET /api/ops/calls', { query: query ?? {} }),
    opsSubscriptions: () => call('GET /api/ops/subscriptions', {}),
    opsCustomers: () => call('GET /api/ops/customers', {}),

    accounts: () => call('GET /api/accounts', {}),
    updateAccount: (id, body) => call('PATCH /api/accounts/:id', { params: { id }, body }),
    connections: () => call('GET /api/connections', {}),

    async loans(asOf) {
      try {
        const payload = normalizeLoansPayload(await request('GET', `${baseUrl}/api/loans`), asOf);
        if (payload) return payload;
      } catch (error) {
        // A server without the route (404 / NOT_IMPLEMENTED) is expected while §6 lands; anything
        // else — no session, a network failure — must reach the screen as itself.
        const missing = error instanceof ApiError && (error.status === 404 || error.code === 'NOT_IMPLEMENTED');
        if (!missing) throw error;
      }
      const accounts = await call('GET /api/accounts', {});
      return loansFromAccounts(accounts.items, asOf);
    },

    settings: () => call('GET /api/settings', {}),
    updateSettings: (body) => call('PATCH /api/settings', { body }),
    testHyp: () => call('POST /api/integrations/hyp/test', {}),
    testLabtop: () => call('POST /api/integrations/labtop/test', {}),
    users: () => call('GET /api/users', {}),

    sync: (providerRefresh) => call('POST /api/sync', { query: providerRefresh ? { providerRefresh: '1' } : {} }),
    syncStatus: () => call('GET /api/sync/status', {}),

    alerts: (query) => call('GET /api/alerts', { query: query ?? {} }),
    updateAlert: (id, body) => call('PATCH /api/alerts/:id', { params: { id }, body }),
    readAllAlerts: (body) => call('POST /api/alerts/read-all', { body: body ?? {} }),

    subscribe(onEvent) {
      if (typeof EventSource === 'undefined') return () => undefined;
      const source = new EventSource(`${baseUrl}/api/events`, { withCredentials: true });
      const names: Array<SseEvent['event']> = ['hello', 'sync:start', 'sync:progress', 'sync:done', 'sync:error', 'alerts:changed', 'data:changed'];
      const handlers = names.map((name) => {
        const handler = (ev: MessageEvent<string>) => {
          try {
            onEvent({ event: name, data: JSON.parse(ev.data) } as SseEvent);
          } catch {
            /* a malformed frame is ignored — the next poll/refresh recovers */
          }
        };
        source.addEventListener(name, handler as EventListener);
        return { name, handler };
      });
      return () => {
        for (const { name, handler } of handlers) source.removeEventListener(name, handler as EventListener);
        source.close();
      };
    },

    exportUrl(kind, query) {
      return `${baseUrl}/api/export/${kind}.csv${queryString(query)}`;
    },

    isMock: false,
  };
}

/**
 * Which adapter to use: the mock is selected by `window.__API_MOCK__ === true` or by `?mock=1`
 * in the URL (`?mock=0` forces the real server). The flag is remembered in `sessionStorage` so
 * in-app navigation keeps it.
 */
export function shouldUseMock(): boolean {
  try {
    const flagged = (globalThis as { __API_MOCK__?: boolean }).__API_MOCK__;
    if (typeof flagged === 'boolean') return flagged;
    if (typeof location !== 'undefined') {
      const q = new URLSearchParams(location.search);
      const value = q.get('mock');
      if (value === '1' || value === 'true') {
        sessionStorage.setItem('lt.mock', '1');
        return true;
      }
      if (value === '0' || value === 'false') {
        sessionStorage.removeItem('lt.mock');
        return false;
      }
    }
    return sessionStorage.getItem('lt.mock') === '1';
  } catch {
    return false;
  }
}
